import { useEffect, useState } from "react";
import AdminHeader from "@/pages/admin/components/AdminHeader";
import ArticleModal, { hasStoredArticleDraft } from "@/pages/admin/components/ArticleModal";
import ConfirmDialog from "@/pages/admin/components/ConfirmDialog";
import { useArticleCatalog, type ArticleRecord } from "@/hooks/useArticleCatalog";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const TYPE_COLORS: Record<string, string> = {
  Review: "bg-emerald-100 text-emerald-700",
  Compra: "bg-amber-100 text-amber-700",
};

export default function AdminArticles() {
  const { articles, addArticle, updateArticle, deleteArticle, resetToDefault } = useArticleCatalog();
  const { settings, updateSettings } = useSiteSettings();
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("Todos");
  const [filterType, setFilterType] = useState("Todos");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<ArticleRecord | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [draftRestoredOnPageLoad, setDraftRestoredOnPageLoad] = useState(false);
  const [guidesDescription, setGuidesDescription] = useState(settings.guidesPageDescription);

  const categories = ["Todos", ...Array.from(new Set(articles.map((a) => a.category)))];
  const types = ["Todos", "Review", "Compra"];

  const filtered = articles.filter((a) => {

    const matchSearch = a.title.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCategory === "Todos" || a.category === filterCategory;
    const matchType = filterType === "Todos" || a.type === filterType;
    return matchSearch && matchCat && matchType;
  });

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    if (draftRestoredOnPageLoad || modalOpen) return;
    if (!hasStoredArticleDraft()) return;

    setEditingArticle(null);
    setModalOpen(true);
    setDraftRestoredOnPageLoad(true);
    showToast("Rascunho de artigo restaurado automaticamente.");
  }, [draftRestoredOnPageLoad, modalOpen]);

  useEffect(() => {
    setGuidesDescription(settings.guidesPageDescription);
  }, [settings.guidesPageDescription]);

  const handleSave = (data: Omit<ArticleRecord, "id">) => {
    if (editingArticle) {
      updateArticle(editingArticle.id, data);
      showToast("Artigo atualizado com sucesso!");
    } else {
      addArticle(data);
      showToast("Artigo adicionado com sucesso!");
    }
    setModalOpen(false);
    setEditingArticle(null);
  };

  const handleEdit = (a: ArticleRecord) => {
    setEditingArticle(a);
    setModalOpen(true);
  };

  const handleDelete = () => {
    if (deletingId !== null) {
      deleteArticle(deletingId);
      setDeletingId(null);
      showToast("Artigo excluído.");
    }
  };

  const handleSaveGuidesDescription = () => {
    const nextValue = guidesDescription.trim();
    setGuidesDescription(nextValue);
    updateSettings({ guidesPageDescription: nextValue });
    showToast("Texto da página de guias atualizado.");
  };

  return (
    <div>
      <AdminHeader
        title="Artigos & Guias"
        subtitle={`${articles.length} artigos cadastrados`}
        action={
          <div className="flex gap-2">
            <button
              onClick={() => { resetToDefault(); showToast("Dados restaurados para o padrão."); }}
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-1.5"></i>Restaurar
            </button>
            <button
              onClick={() => { setEditingArticle(null); setModalOpen(true); }}
              className="px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-1.5"></i>Novo Artigo
            </button>
          </div>
        }
      />

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-5 md:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <h2 className="text-lg font-black text-gray-900 flex items-center gap-2">
              <i className="ri-text text-orange-500"></i>
              Texto descritivo da página /guias
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              O texto salvo aqui será exibido abaixo do título principal na página pública /guias.
            </p>
          </div>
          <button
            onClick={handleSaveGuidesDescription}
            className="px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-save-line mr-1.5"></i>Salvar texto
          </button>
        </div>

        <textarea
          value={guidesDescription}
          onChange={(e) => setGuidesDescription(e.target.value)}
          rows={3}
          className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm resize-none focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          placeholder="Digite o texto exibido abaixo do título da página /guias"
        />
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-48">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar artigo..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 cursor-pointer"
        >
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 cursor-pointer"
        >
          {types.map((t) => <option key={t}>{t}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {filtered.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 py-16 text-center text-gray-400">
            <i className="ri-article-line text-4xl block mb-2"></i>
            <p className="text-sm">Nenhum artigo encontrado</p>
          </div>
        )}
        {filtered.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-4 hover:border-orange-200 transition-all">
            <img
              src={a.image}
              alt={a.title}
              className="w-20 h-14 rounded-xl object-cover flex-shrink-0 bg-gray-100"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${TYPE_COLORS[a.type] || "bg-gray-100 text-gray-600"}`}>
                  {a.type}
                </span>
                <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-0.5 rounded-full">{a.category}</span>
                {a.featured && (
                  <span className="text-xs font-semibold text-orange-500 bg-orange-50 px-2.5 py-0.5 rounded-full">
                    <i className="ri-star-line mr-1"></i>Destaque
                  </span>
                )}
                {a.trending && (
                  <span className="text-xs font-semibold text-red-500 bg-red-50 px-2.5 py-0.5 rounded-full">
                    <i className="ri-fire-line mr-1"></i>Trending
                  </span>
                )}
              </div>
              <p className="text-sm font-semibold text-gray-800 truncate">{a.title}</p>
              <p className="text-xs text-gray-400 mt-0.5 truncate">{a.excerpt}</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="text-right">
                <p className="text-xs text-gray-500">{a.readTime}</p>
                <p className="text-xs text-gray-400">{a.views.toLocaleString("pt-BR")} views</p>
              </div>
              <a
                href={`/artigo/${a.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-sm"></i>
              </a>
              <button
                onClick={() => handleEdit(a)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-orange-50 text-gray-400 hover:text-orange-500 transition-colors cursor-pointer"
              >
                <i className="ri-edit-line text-sm"></i>
              </button>
              <button
                onClick={() => setDeletingId(a.id)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            </div>
          </div>
        ))}
      </div>

      {modalOpen && (
        <ArticleModal
          article={editingArticle}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditingArticle(null); }}
        />
      )}

      {deletingId !== null && (
        <ConfirmDialog
          title="Excluir Artigo"
          message="Tem certeza que deseja excluir este artigo? Esta ação não pode ser desfeita."
          onConfirm={handleDelete}
          onCancel={() => setDeletingId(null)}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-xl z-50">
          <i className="ri-checkbox-circle-line text-green-400 mr-2"></i>
          {toast}
        </div>
      )}
    </div>
  );
}
